import React from 'react';
import {
  RegistrationScreen,
  type RegistrationConfig,
} from './registration-common.js';

/**
 * The Hogan swivel-chair hint shown on each of the 6 mortgage questions
 * (CCF-59, issue #319) — there is NO API integration for any of them (unlike
 * Borrower address, which `customer/identification` DOES cover): the agent
 * verifies the answer directly against Hogan (a separate core-banking system)
 * and records Pass/Fail with no on-file value ever shown, mirroring the
 * identical ruling already applied to the Mortgage Security screen
 * (SCRN-09/CCF-24, issue #309).
 */
const HOGAN_HINT = 'Verify against Hogan.';

/**
 * SCRN 20 — Mortgage Registration (CCF-59). Mirrors the Figma
 * `update-reg-mortgage` design: Full Name + Borrower address mandatory identity
 * items, then any 3 of 6 mortgage questions (validated against Hogan), 2FA,
 * mandatory scripting and "Transfer to Automated Self Service". Reached after a
 * deregistration when the online-card type is MORTGAGE (CCF-8/CCF-46).
 *
 * Borrower address is sourced from `customer/identification`'s `BorrowerAddress`
 * (CCF-59's own APIs table: "Borrower address covered") — NOT `PropertyAddress`,
 * a Mortgage-only mock convenience field with no real-gateway equivalent (the
 * mock-only `customer/security-questions` op 500s on the real gateway, issue
 * #76). The 6 additional questions have no API integration at all — CCF-59
 * says the agent "validates against Hogan" — so they are modelled as
 * `externalCheck` (question + Pass/Fail only, never an on-file value or an
 * "Answer not on file" fallback), mirroring the identical fix already applied
 * to the Mortgage Security screen (issue #309). Both fixes land together under
 * issue #319, which reported every field except Full Name showing "Answer not
 * on file" on staging (the exact symptom of sourcing them all from the
 * mock-only op).
 */
const CONFIG: RegistrationConfig = {
  screenCode: 'SCRN-20',
  heading: 'Open24 registration - Mortgage only',
  mandatory: [
    { key: 'name', label: 'Full name', value: (_q, name) => name },
    {
      key: 'borrowerAddress',
      label: 'Borrower address',
      value: (q) => q.BorrowerAddress,
    },
  ],
  additional: [
    {
      key: 'branchOrBroker',
      label: 'Was the mortgage taken out in branch or with a broker?',
      hint: HOGAN_HINT,
      externalCheck: true,
    },
    {
      key: 'ddBank',
      label: 'If paying by Direct Debit, which bank is the DD paid from?',
      hint: HOGAN_HINT,
      externalCheck: true,
    },
    {
      key: 'drawdown',
      label: 'Mortgage drawdown date',
      hint: HOGAN_HINT,
      externalCheck: true,
    },
    {
      key: 'term',
      label: 'Original term of the mortgage',
      hint: HOGAN_HINT,
      externalCheck: true,
    },
    {
      key: 'homeInsurance',
      label: 'Home insurance provider',
      hint: HOGAN_HINT,
      externalCheck: true,
    },
    {
      key: 'lifeAssurance',
      label: 'Life assurance provider',
      hint: HOGAN_HINT,
      externalCheck: true,
    },
  ],
  requiredAdditional: 3,
  // Issue #350 (dylan219): the agent selects EXACTLY 3 of the 6 questions via
  // a tick-box in front of each; only a ticked question's Pass/Fail control is
  // shown, a 4th selection is prevented, and the outcome passes only when all
  // 3 selected are marked Pass.
  additionalSelectableLimit: 3,
  additionalInstructions: 'Select any 3 of 6. Check Hogan for all answers.',
  twoFactor: true,
  // Issue #774 (@dylan219, 2026-09-16): an "Unable to complete 2FA" tick box
  // BESIDE the Send Code button. Ticking it marks 2FA a FAIL — recorded
  // SERVER-SIDE against the session-bound Registration Security run (never a
  // client-declared flag, the #729 lesson) — while the fail outcome/messaging
  // (FAIL badge + branch referral) is DEFERRED until every other question is
  // completed, reusing the #781 deferral the Broker24 journey established.
  // ECL-flow gated server-side (the recording route carries the dedicated,
  // ECLAgent-only `skipRegistration2fa` permission) so the box renders for an
  // ECL agent, fail-closed hidden while the server session role resolves.
  unableToComplete2faTickBox: true,
  // Issue #680 (@philipc39): the mortgage registration screen must NOT show
  // the "Please leave a note that the customer has been referred to branch."
  // instruction on the failed-registration banner.
  hideReferralNote: true,
  // #729 (@dylan219, 2026-08-31) — once all questions + 2FA pass, offer the
  // Reset Internet Password option with the scoped Back round-trip, in the
  // same way and under the same rules as Business24 / Broker24 (#653). The
  // underlying gateway reset flow is PTSB-side pending; the option is wired in
  // parallel with their gateway fix and surfaces whatever the gateway returns.
  ipwReset: true,
};

export function MortgageRegistrationScreen(): React.JSX.Element {
  return <RegistrationScreen config={CONFIG} />;
}
